import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Shared product state for the product BLOCKS (title, price, variant-picker, quantity, buy-buttons,
 * description…). In Shrine these pieces sit at different depths of the product tree yet must stay in
 * sync (pick a variant → price/buy-buttons update). The old main-product section held this in
 * closures over its switch; with blocks extracted into independent registry components, the same
 * state lives in a React context provided once by main-product (and any future product container).
 *
 * SSR-safe: a provider holds `useState`, so the server renders the initial selection and hydration
 * just attaches the handlers — identical output to the previous closure model.
 */
import { DEFAULT_COD_CONFIG, buyerEmailOf } from '@aquilixshop/cod-config';
import { t } from '@aquilixshop/i18n';
import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore, } from 'react';
import { pickerVariants, resolveProduct, } from '../../sections/_shared/product.js';
import { DROPPED_UPSELLS_KEY, placeCodOrder, } from '../../sections/cod-buybox/submit.js';
import { groupUnitLines } from './unitVariants.js';
import { createPaymentIntent, devStoreParam, fetchCardEnabled, makeIdempotencyKey, } from '../../sections/_shared/paymentClient.js';
/**
 * The basket as `(variantId, quantity)` lines: the per-unit variants grouped, first-seen order. This is THE
 * shape the checkout, the cart quote and the discount preview price from — a pack of three Medium is
 * `[{M, 3}]`, byte-identical to the historic single line; one Medium and two Large is `[{M, 1}, {L, 2}]`.
 */
export function basketLines(state) {
    const lines = groupUnitLines(state.unitVariantIds);
    if (lines.length > 0)
        return lines;
    // No resolvable unit (a product with no variants at all): the historic single line, or nothing.
    return state.variantId ? [{ variantId: state.variantId, quantity: state.quantity }] : [];
}
/**
 * `[unitVariantIds, setUnitVariantId]` for a provider. Explicit choices live in a map keyed by unit index
 * (state, so SSR and hydration both start EMPTY → byte-identical markup); the effective list is derived on
 * every render from that map, the shared variant and the quantity. A choice that names a variant which no
 * longer exists (the product was re-resolved) falls back to the shared variant rather than sticking.
 */
function useUnitVariants(quantity, sharedVariantId, variants) {
    const [chosen, setChosen] = useState({});
    const count = Math.max(0, Math.floor(quantity));
    const unitVariantIds = [];
    for (let i = 0; i < count; i += 1) {
        const pick = chosen[i];
        unitVariantIds.push(pick && variants.some((v) => v.id === pick) ? pick : sharedVariantId);
    }
    const setUnitVariantId = useCallback((index, variantId) => {
        if (index < 0 || !Number.isInteger(index))
            return;
        setChosen((prev) => (prev[index] === variantId ? prev : { ...prev, [index]: variantId }));
    }, []);
    return [unitVariantIds, setUnitVariantId];
}
/**
 * THE VARIANT ACTUALLY SELECTED — always one that exists, or '' for a product with no variants.
 *
 * `variantId` is seeded by `useState` when the provider MOUNTS. When the product arrives afterwards (the
 * builder binds it a beat after boot; any host that resolves the product asynchronously) the seed was ''
 * and nothing ever re-seeds it, so every consumer that looks the id up strictly finds nothing:
 *   • media-gallery showed every colour at once instead of the selected one,
 *   • sku printed the product slug instead of the variant reference,
 *   • cod-form's Commander button returned early and did NOTHING, silently.
 *
 * Resolved HERE rather than in each block: the blocks must agree on what is selected, and three of them
 * had already grown their own `?? variants[0]` while the others had not — which is exactly how they came
 * to disagree. `setVariantId` still writes the raw state; only what consumers READ is normalised.
 */
function effectiveVariantId(variantId, variants) {
    if (variants.some((v) => v.id === variantId))
        return variantId;
    return variants[0]?.id ?? '';
}
/**
 * THE SELECTED VARIANT IS SHARED ACROSS REACT ROOTS, not merely across blocks.
 *
 * The storefront product page is SSR'd as ONE provider but hydrated as TWO INDEPENDENT roots — the buy-box
 * column and the media column (see the storefront's `mainproduct-island.ts`). Each root rebuilt its own
 * provider, so each held its OWN `useState`: clicking a colour moved the picker and left the gallery on the
 * previous photo, for good, while the builder — one tree, one provider — looked perfect. A React context
 * cannot cross roots; the selection therefore lives in this module store, keyed by product, and EVERY
 * provider of that product reads and writes the same entry.
 *
 * SSR/hydration-safe: the store starts EMPTY, so the server snapshot and the client's hydration snapshot are
 * both '' and `effectiveVariantId` resolves both to `variants[0].id` — byte-identical markup, no mismatch.
 * Keyed per product so a `featured-product` block showing a DIFFERENT product keeps its own selection.
 */
const selectedVariants = new Map();
const variantListeners = new Map();
/** The store key for a product — its id, falling back to the slug for the synthetic demo/fallback products. */
function variantKey(product) {
    return product.id || product.slug || 'default';
}
/**
 * Subscribing doubles as the reference count: while at least one provider of this product is mounted the
 * selection is shared, and when the LAST one leaves the entry is dropped. So the store never outlives the
 * page that owns it — a product remounted later starts on its first variant again, exactly like the
 * `useState` this replaces, and nothing leaks from one mounted tree to the next.
 */
function subscribeVariant(key, notify) {
    let set = variantListeners.get(key);
    if (!set) {
        set = new Set();
        variantListeners.set(key, set);
    }
    set.add(notify);
    return () => {
        set?.delete(notify);
        if (set && set.size === 0) {
            variantListeners.delete(key);
            selectedVariants.delete(key);
        }
    };
}
/**
 * `[variantId, setVariantId]` backed by the cross-root store. Same signature as `useState<string>` (functional
 * updates included), so the providers below are untouched apart from the call itself.
 */
function useSharedVariantId(key) {
    const subscribe = useCallback((notify) => subscribeVariant(key, notify), [key]);
    const variantId = useSyncExternalStore(subscribe, () => selectedVariants.get(key) ?? '', () => '');
    const setVariantId = useCallback((next) => {
        const prev = selectedVariants.get(key) ?? '';
        const value = typeof next === 'function' ? next(prev) : next;
        if (value === prev)
            return;
        selectedVariants.set(key, value);
        variantListeners.get(key)?.forEach((notify) => notify());
    }, [key]);
    return [variantId, setVariantId];
}
const ProductCtx = createContext(null);
/**
 * The cart quantity a product buy-box starts with. When THIS provider's product carries an active quantity offer
 * (feature 0026-07), the initial quantity is the PRESELECTED tier's pack size (fallback: the first tier's) — so
 * the highlighted offer tile is the one that matches `quantity` on the VERY FIRST render, on both SSR and
 * hydration (seeded here at the provider, NOT in a mount effect that would repaint post-hydration and desync the
 * SSR'd selection). Gated on `activeOffer.productId === productId` because `context.activeOffer` is the ROUTE
 * product's offer: a `featured-product` provider (a DIFFERENT product) must NOT seed the route offer's tier qty.
 * No / mismatched / empty offer → 1, exactly as before (fail-open).
 */
function initialQuantity(context, productId) {
    const offer = context.activeOffer;
    if (!offer || offer.productId !== productId)
        return 1;
    const tiers = offer.tiers;
    if (!tiers || tiers.length === 0)
        return 1;
    const chosen = tiers.find((t) => t.preselect) ?? tiers[0];
    return chosen?.qty ?? 1;
}
/**
 * Provide the shared product/variant/quantity state to every descendant product block. Resolves the
 * route-bound product (or the demo fallback) ONCE — exactly as the section did before. When an explicit
 * `product` is passed (the `featured-product` section, which shows a PICKED product rather than the route
 * product), that product is used and its picker variants are derived from its options.
 */
export function ProductStateProvider({ context, product: productOverride, children, }) {
    // `pickerVariants`, not `variantsFromOptions`: when the overriding product carries its REAL variants
    // (a buy-box picked on a landing page, resolved with its variant matrix) those UUIDs are what checkout
    // accepts. It falls back to the option-derived list when the host loaded none, so `featured-product`
    // (whose grid-shaped products carry no variants) renders exactly as before.
    const { product, variants } = productOverride
        ? { product: productOverride, variants: pickerVariants(productOverride) }
        : resolveProduct(context);
    const [variantId, setVariantId] = useSharedVariantId(variantKey(product));
    const [quantity, setQuantity] = useState(() => initialQuantity(context, product.id));
    const [message, setMessage] = useState('');
    const effectiveId = effectiveVariantId(variantId, variants);
    const [unitVariantIds, setUnitVariantId] = useUnitVariants(quantity, effectiveId, variants);
    return (_jsx(ProductCtx.Provider, { value: {
            product,
            variants,
            variantId: effectiveId,
            setVariantId,
            quantity,
            setQuantity,
            unitVariantIds,
            setUnitVariantId,
            message,
            setMessage,
        }, children: children }));
}
/**
 * Read the shared product state. Returns null OUTSIDE a provider (e.g. a product block dropped into a
 * non-product container) — a product block should then render nothing rather than throw.
 */
export function useProductState() {
    return useContext(ProductCtx);
}
const CheckoutCtx = createContext(null);
const EMPTY_FORM = { name: '', phone: '', city: '', address: '', note: '' };
/**
 * Provide the shared product state AND the checkout slice to a `main-product` buy-box subtree. Resolves the
 * route product (or the demo fallback) exactly like {@link ProductStateProvider}, then layers the checkout
 * state + `placeOrder()` action so the separate buy-buttons / cod-form / card blocks coordinate one order.
 *
 * SSR-safe: the card-status fetch + the idempotency-key mint live in mount effects (never run server-side),
 * and neither `cardEnabled` (false) nor the empty form / idle status render any DOM the SSR pass doesn't —
 * so the hydrated tree is byte-identical to the SSR HTML (no hydration mismatch).
 */
export function CheckoutProductStateProvider({ context, product: productOverride, demo, children, }) {
    const resolved = productOverride
        ? { product: productOverride, variants: pickerVariants(productOverride), isDemo: demo === true }
        : resolveProduct(context);
    // `hidden` = the live fallback product (no route product): its ids are synthetic exactly like the demo's,
    // so it must never reach the real checkout either — treat it as demo for every submission/hydration gate.
    const { product, variants } = resolved;
    const isDemo = resolved.isDemo || resolved.hidden === true;
    // Product state (shared with the plain provider's consumers — and, via the module store, with the OTHER
    // hydrated root on the storefront product page: the media column).
    const [variantId, setVariantId] = useSharedVariantId(variantKey(product));
    const [quantity, setQuantity] = useState(() => initialQuantity(context, product.id));
    const [message, setMessage] = useState('');
    const effectiveId = effectiveVariantId(variantId, variants);
    const [unitVariantIds, setUnitVariantId] = useUnitVariants(quantity, effectiveId, variants);
    // Checkout state.
    const [form, setForm] = useState(EMPTY_FORM);
    const [promo, setPromo] = useState(null);
    const [status, setStatus] = useState('idle');
    const [checkoutMessage, setCheckoutMessage] = useState('');
    // Seeded EMPTY, minted AFTER mount (client-only) — never rendered into the DOM, so '' during SSR keeps
    // the hydrated tree byte-identical. Same fix as cod-buybox.
    const [idempotencyKey, setIdempotencyKey] = useState('');
    // Seed from the bundle's enabledPaymentMethods (SSR + hydrated client get the SAME value — it rides the
    // island props — so seeding true keeps SSR/first-paint identical). The post-mount probe below still upgrades
    // false→true as a fail-safe when the bundle omitted it.
    const [cardEnabled, setCardEnabled] = useState((context.enabledPaymentMethods ?? []).includes('CARD'));
    const [paymentMethod, setPaymentMethod] = useState('COD');
    const [cardIntent, setCardIntent] = useState(null);
    // Mint the idempotency key after mount (client-only) — kept out of render so SSR/client first paint match.
    useEffect(() => {
        setIdempotencyKey((k) => k || makeIdempotencyKey());
    }, []);
    // Learn whether this store accepts cards (live storefront only; skipped in the builder + offline demo).
    useEffect(() => {
        if (context.renderMode === 'edit' || isDemo || typeof window === 'undefined')
            return;
        let cancelled = false;
        void (async () => {
            const enabled = await fetchCardEnabled();
            if (!cancelled && enabled)
                setCardEnabled(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [context.renderMode, isDemo]);
    async function placeOrder(opts) {
        // Required-field guard derived from the seller's COD config (client-side; the backend re-validates).
        const cfg = context.codConfig ?? DEFAULT_COD_CONFIG;
        const requiredIds = cfg.fields.filter((f) => f.enabled && f.required).map((f) => f.id);
        const missing = requiredIds.find((id) => !(form[id] ?? '').trim());
        if (missing) {
            setStatus('error');
            setCheckoutMessage('Veuillez remplir tous les champs obligatoires.');
            return;
        }
        setStatus('submitting');
        setCheckoutMessage('');
        // Ensure a stable idempotency key exists (guards a submit racing the mount effect); persist it so a CARD
        // retry dedupes to the SAME order + intent.
        let key = idempotencyKey;
        if (!key) {
            key = makeIdempotencyKey();
            setIdempotencyKey(key);
        }
        // The buy-box block picks the single method for this page and passes it in; fall back to the shared state.
        const isCard = (opts?.method ?? paymentMethod) === 'CARD' && cardEnabled;
        // The buyer's EMAIL is not a note line — it is the order's email (16/10/2026). A COD form has no core
        // email field, so a seller who wants one adds a field of TYPE `email`; its id is generated (`custom-6`)
        // and nothing downstream could guess it. Until today it was folded into the note like any other custom
        // field: `orders.customer_email` stayed NULL, the order page showed "—", and every journey hung on that
        // order was skipped with NO_EMAIL — the seller's "order not confirmed" email could never leave.
        const buyerEmail = buyerEmailOf(cfg, form);
        // Fold the REMAINING enabled custom fields into the note (the one free-text field the backend already
        // accepts), so a seller's custom fields reach the order with no backend contract change. An email field
        // whose value is NOT an address stays a note line: the seller still sees what the buyer typed, and a
        // typo in an optional field can never cost the order a 400 on the backend's `@Email`.
        const extraLines = cfg.fields
            .filter((f) => !f.core && f.enabled && (form[f.id] ?? '').trim())
            .filter((f) => !(f.type === 'email' && (form[f.id] ?? '').trim() === buyerEmail))
            .map((f) => `${f.label}: ${form[f.id]}`);
        const note = [form.note?.trim(), ...extraLines].filter(Boolean).join('\n');
        const outcome = await placeCodOrder({
            form: { ...form, note, email: buyerEmail },
            variantId: effectiveId,
            quantity,
            // The per-unit variants, grouped (28/09/2026): `[{M,1},{L,2}]` for a mixed pack of three, and the
            // historic single line when nobody touched a unit row — the server picks the tier on the TOTAL.
            lines: basketLines({ unitVariantIds, variantId: effectiveId, quantity }),
            idempotencyKey: key,
            paymentMethod: isCard ? 'CARD' : 'COD',
            // Accepted upsell offers (feature 0042 lot D) ride ALONGSIDE the cart line, never inside it: the
            // server reads the stored offer and builds the extra lines itself. Absent when nothing was accepted.
            upsell: opts?.upsell,
            // The applied promo code (feature 0014) — code only, never an amount: the backend re-prices it
            // with the same engine that validated the preview, so a shaped body cannot mint a discount.
            discountCode: promo?.code ?? null,
            // Dev `?store=` hint so the checkout proxy resolves the tenant on localhost (the CARD path already
            // passes this to createPaymentIntent). Ignored in prod (store comes from the subdomain Host).
            store: devStoreParam(),
        }, { onPlaceOrder: () => { } });
        // ── CARD path: the order is placed UNPAID; create its PaymentIntent, then reveal the card element. The
        // idempotency key is NOT regenerated (a retry must dedupe to the SAME order + intent). PAID is asserted
        // only by the backend webhook — here we just hand off to Stripe.
        if (isCard && outcome.kind === 'ok') {
            if (!outcome.orderId) {
                setStatus('error');
                setCheckoutMessage(t(context, 'error.generic'));
                return;
            }
            const intent = await createPaymentIntent(outcome.orderId, devStoreParam());
            if (intent.kind === 'ok') {
                setCardIntent({
                    clientSecret: intent.clientSecret,
                    stripeAccountId: intent.stripeAccountId,
                    // Feature 0069 lot D — rides to the return URL so the buyer comes back to their real confirmation.
                    orderId: outcome.orderId,
                });
                setStatus('idle'); // reveal the Payment Element; it owns its own confirm/redirect
                setCheckoutMessage('');
            }
            else if (intent.kind === 'not-payable') {
                setStatus('error');
                setCheckoutMessage(t(context, 'error.already_paid'));
            }
            else if (intent.kind === 'rate-limited') {
                setStatus('error');
                setCheckoutMessage(t(context, 'error.too_many_attempts_dot'));
            }
            else {
                setStatus('error');
                setCheckoutMessage(t(context, 'error.card_unavailable'));
            }
            return;
        }
        if (outcome.kind === 'ok') {
            setStatus('done');
            setIdempotencyKey(makeIdempotencyKey()); // a confirmed order → allow a fresh, intentional next order
            // Real order (feature 0031): leave the in-place card and navigate to the dedicated thank-you page. Guard
            // on a live storefront + a real order id — the builder/demo path is `placeholder`, never `ok` with an id,
            // so the preview never navigates. The in-place message below stays as a fallback if navigation can't run.
            if (!isDemo && outcome.orderId && typeof window !== 'undefined') {
                // Offers the buyer accepted that the server could not honour (feature 0042 lot D). We are about to
                // navigate away, so the fact is handed to the confirmation page rather than shown here — a buyer who
                // chose something must be told it is not in their order, even though they are charged less for it.
                const dropped = outcome.droppedUpsellOffers?.length ?? 0;
                if (dropped > 0) {
                    try {
                        window.sessionStorage.setItem(DROPPED_UPSELLS_KEY, String(dropped));
                    }
                    catch {
                        // A blocked sessionStorage costs a notice, never the order.
                    }
                }
                const params = new URLSearchParams({ order: outcome.orderId });
                const store = new URLSearchParams(window.location.search).get('store');
                if (store)
                    params.set('store', store);
                const proceed = () => window.location.assign(`/checkout/confirmation?${params.toString()}`);
                // A caller may hold this jump to play the post-purchase offer where the buyer already is — in the
                // buy-box, not on the thank-you page (feature 0042 lot E, founder 2026-08-12). The ORDER is already
                // placed either way; only the navigation waits, and only until `proceed` is called.
                if (opts?.onPlaced)
                    opts.onPlaced(outcome.orderId, proceed);
                else
                    proceed();
                return;
            }
            const totalStr = outcome.total ? ` — ${context.formatMoney(outcome.total)}` : '';
            setCheckoutMessage(outcome.orderNumber
                ? t(context, 'checkout.order_confirmed_with_number', {
                    number: outcome.orderNumber,
                    total: totalStr,
                })
                : t(context, 'checkout.order_confirmed'));
        }
        else if (outcome.kind === 'placeholder' && isDemo) {
            // Builder preview / offline demo: the proxy isn't reachable → complete the flow so the founder can demo
            // the full UX. On a LIVE storefront a network failure must NOT read as a fake success (falls through).
            setStatus('done');
            setCheckoutMessage(t(context, 'checkout.order_recorded_demo'));
        }
        else if (outcome.kind === 'validation-error') {
            setStatus('error');
            setCheckoutMessage(t(context, 'error.check_your_details'));
        }
        else {
            setStatus('error');
            setCheckoutMessage(t(context, 'error.generic'));
        }
    }
    return (_jsx(ProductCtx.Provider, { value: {
            product,
            variants,
            variantId: effectiveId,
            setVariantId,
            quantity,
            setQuantity,
            unitVariantIds,
            setUnitVariantId,
            message,
            setMessage,
        }, children: _jsx(CheckoutCtx.Provider, { value: {
                form,
                setForm,
                promo,
                setPromo,
                status,
                message: checkoutMessage,
                cardEnabled,
                paymentMethod,
                setPaymentMethod,
                cardIntent,
                isDemo,
                placeOrder,
            }, children: children }) }));
}
/**
 * Read the shared checkout state. Returns null when the block is NOT under a
 * {@link CheckoutProductStateProvider} (e.g. `featured-product`, which uses the plain provider) — the
 * cod-form then renders nothing and buy-buttons falls back to its demo add-to-cart behaviour.
 */
export function useCheckoutState() {
    return useContext(CheckoutCtx);
}
//# sourceMappingURL=productContext.js.map